/**
 * PlanProgressBar
 *
 * Stage progress pipeline rendered at the top of every plan card. Mirrors the
 * legacy plan-stage-pipeline.php exactly:
 *  - One pill per `accounts_custom_stages` row, in `orderNum` order.
 *  - Visible row hides any pill flagged `hiddenList=true`; hidden + `moveTo`
 *    pills are accessible via a trailing "..." popover.
 *  - Pill state per pill: `selected` (filled), `moveTo` (clickable target),
 *    or static (greyed out, not clickable).
 *  - Click on a `moveTo` pill PUTs `{ stageId }` to /plans/{id}; the click
 *    sends `stageId` if the pill maps to a global stage, otherwise the
 *    `accountCustomStageId` directly (mirrors legacy `data-stagetype`).
 *  - When the response carries a `newPlanId` (legacy stage 12 revision spawn),
 *    the parent is notified to swap the card to the new plan id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Loader2, MoreHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import * as plansSvc from '@/services/plans.service';
import type { PlanProgressStage } from '@/services/plans.service';

const PILL_MIN_W = 92;
const OVERFLOW_BTN_W = 42;

interface PlanProgressBarProps {
  planId: number;
  patientId?: number;
  /** Called with the new plan id when a stage change spawns a revision. */
  onPlanIdChange?: (newPlanId: number) => void;
  /** When true, pills stretch evenly to fill the full container width (legacy pipeline parity). */
  stretch?: boolean;
}

function pillVisualClasses(stage: PlanProgressStage, clickable: boolean): string {
  if (stage.selected) {
    // Legacy: #3a7fd5
    return 'text-white shadow-sm' + ' bg-[#3a7fd5]';
  }
  if (stage.moveTo) {
    // Legacy "dark": #888, hover #555
    return clickable
      ? 'text-white cursor-pointer bg-[#888] hover:bg-[#555]'
      : 'text-white opacity-70 bg-[#888]';
  }
  // Legacy default: #bbb with white text
  return 'text-white bg-[#bbb]';
}

export function PlanProgressBar({ planId, patientId, onPlanIdChange, stretch }: PlanProgressBarProps) {
  const queryClient = useQueryClient();
  const [pendingPillKey, setPendingPillKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plan-progress-bar', planId],
    queryFn: () => plansSvc.getPlanProgressBar(planId),
    enabled: Number.isFinite(planId) && planId > 0,
    staleTime: 30_000,
  });

  const stageMutation = useMutation({
    mutationFn: ({ targetStageId }: { pillKey: string; targetStageId: number }) =>
      plansSvc.changePlanStage(planId, { stageId: targetStageId }),
    onSettled: () => {
      setPendingPillKey(null);
    },
    onSuccess: (updated) => {
      const newPlanId = updated.newPlanId;
      const isRevisionSpawn = typeof newPlanId === 'number' && newPlanId !== planId;

      if (isRevisionSpawn && newPlanId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['plan-progress-bar', newPlanId] });
        queryClient.invalidateQueries({ queryKey: ['plan-stage-subs', newPlanId] });
      }

      queryClient.invalidateQueries({ queryKey: ['plan-progress-bar', planId] });
      queryClient.invalidateQueries({ queryKey: ['plan-stage-subs', planId] });
      if (patientId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
      }

      if (isRevisionSpawn && newPlanId !== undefined) {
        onPlanIdChange?.(newPlanId);
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to change plan stage', err);
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { visiblePills, hiddenPills } = useMemo(() => {
    const stages = data?.stages ?? [];
    const candidates: PlanProgressStage[] = [];
    const apiHidden: PlanProgressStage[] = [];
    for (const s of stages) {
      if (s.hiddenList) {
        if (s.moveTo) apiHidden.push(s);
      } else {
        candidates.push(s);
      }
    }

    if (candidates.length === 0) {
      return { visiblePills: candidates, hiddenPills: apiHidden };
    }

    if (containerWidth <= 0) {
      return { visiblePills: [], hiddenPills: [...candidates, ...apiHidden] };
    }

    const needsOverflowBtn = apiHidden.length > 0 || candidates.length > Math.floor(containerWidth / PILL_MIN_W);
    const maxVisible = Math.max(1, Math.floor(
      (containerWidth - (needsOverflowBtn ? OVERFLOW_BTN_W : 0)) / PILL_MIN_W,
    ));

    if (candidates.length <= maxVisible) {
      return { visiblePills: candidates, hiddenPills: apiHidden };
    }

    const selectedIdx = candidates.findIndex((s) => s.selected);
    const safeSelectedIdx = selectedIdx >= 0 ? selectedIdx : 0;

    const startIdx = Math.max(0, Math.min(safeSelectedIdx, candidates.length - maxVisible));
    const endIdx = startIdx + maxVisible;

    const visible = candidates.slice(startIdx, endIdx);
    const overflow = [
      ...candidates.slice(0, startIdx),
      ...candidates.slice(endIdx),
      ...apiHidden,
    ];

    return { visiblePills: visible, hiddenPills: overflow };
  }, [data, containerWidth]);

  const isMutating = stageMutation.isPending;
  const hasData = !isLoading && data && data.stages.length > 0;

  function pillKey(stage: PlanProgressStage): string {
    return `${stage.accountCustomStageId}:${stage.stageId}`;
  }

  function handleClick(stage: PlanProgressStage) {
    if (!stage.moveTo || stage.selected || isMutating) return;
    const target = stage.stageId > 0 ? stage.stageId : stage.accountCustomStageId;
    if (target <= 0) return;
    const key = pillKey(stage);
    setPendingPillKey(key);
    stageMutation.mutate({ pillKey: key, targetStageId: target });
  }

  return (
    <div ref={containerRef} className={stretch ? '' : 'rounded-lg border border-slate-200 bg-white p-3'}>
      {!hasData ? null : (
      <div className={`flex items-center flex-nowrap ${stretch ? 'w-full gap-x-0.5' : 'gap-x-1'}`}>
        {visiblePills.map((stage, idx) => {
          const clickable = stage.moveTo && !stage.selected && !isMutating;
          const key = pillKey(stage);
          const isPending = pendingPillKey === key && isMutating;
          return (
            <div key={`v-${key}-${idx}`} className={`flex items-center gap-x-0.5 ${stretch ? 'flex-1 min-w-[90px]' : ''}`}>
              <button
                type="button"
                onClick={() => handleClick(stage)}
                disabled={!clickable}
                aria-pressed={stage.selected}
                aria-label={stage.label}
                className={`flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium transition-colors disabled:cursor-default min-w-[90px] ${stretch ? 'w-full' : ''} ${pillVisualClasses(
                  stage,
                  clickable,
                )}`}
              >
                {isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <span className="truncate">{stage.label}</span>
                )}
              </button>
              {stage.showArrow && idx < visiblePills.length - 1 && (
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
              )}
            </div>
          );
        })}

        {hiddenPills.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="More stages"
                className="flex h-9 w-9 items-center justify-center rounded-md bg-[#bbb] text-white hover:bg-[#999]"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="flex flex-col gap-1">
                {hiddenPills.map((stage) => {
                  const key = pillKey(stage);
                  const isPending = pendingPillKey === key && isMutating;
                  return (
                    <button
                      key={`h-${key}`}
                      type="button"
                      onClick={() => handleClick(stage)}
                      disabled={isMutating}
                      className="flex items-center justify-between rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-default disabled:opacity-50"
                    >
                      <span className="truncate">{stage.label}</span>
                      {isPending && (
                        <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      )}
    </div>
  );
}

export default PlanProgressBar;
